<template>
    <v-form v-if="showDocument" ref="entityEditForm">
        <v-container>
            <v-row>
                <v-col cols="12" md="4" order-md="3">
                    <div class="d-flex justify-center justify-md-end">
                        <action-buttons
                            :state="editState"
                            :permissions="userPermission"
                            :disabled="disableButtons"
                            @save="handleSave"
                            @cancel="handleCancel"
                            @close="handleClose"
                            :is-dirty="isDirty"
                            class="d-flex justify-end"
                        />
                        <v-btn
                            v-if="toursEnabled"
                            icon
                            small
                            @click="replayEditorTour"
                            title="Replay tour"
                            aria-label="Replay tour"
                            class="ml-1"
                        >
                            <v-icon small>mdi-help-circle-outline</v-icon>
                        </v-btn>
                    </div>
                </v-col>
                <v-col cols="12" md="4" order-md="1" v-if="!isNew">
                    <last-edit-details :entity="doc" />
                </v-col>
                <v-col cols="12" md="4" order-md="2">
                    <team-picker
                        v-if="showTeamPicker"
                        v-model="doc.teamId"
                        :teams="userTeams(entity)"
                        label="Select Team"
                        dense
                        solo
                        hint="Team this is assigned to"
                        persistent-hint
                    ></team-picker>
                </v-col>
            </v-row>
            <v-row v-if="errorMessage" justify>
                <v-col>
                    <v-alert type="error">
                        {{ errorMessage }}
                    </v-alert>
                </v-col>
            </v-row>
            <v-row v-if="isLimitedEdit">
                <v-col cols="12">
                    <v-alert type="info" dense text>
                        You can edit a limited set of fields. Saving will submit this {{ entity }} for approval.
                    </v-alert>
                </v-col>
            </v-row>
            <slot v-if="isLimitedEdit" name="limited-editor" v-bind:doc="doc"></slot>
            <slot v-else name="editor" v-bind:doc="doc"></slot>
            <v-row>
                <v-col cols="12">
                    <div class="d-flex justify-center justify-md-end">
                        <action-buttons
                            :state="editState"
                            :permissions="userPermission"
                            :disabled="disableButtons"
                            @save="handleSave"
                            @cancel="handleCancel"
                            @close="handleClose"
                            :is-dirty="isDirty"
                            class="d-flex justify-end"
                        />
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>
<script>
import api from '@/api'
import { mapState, mapGetters } from 'vuex'
import entities from './entities'
import permissionsSet from './permissions'
import { startEditorCloseTour, startEditorDirtyTour, resetEditorTours, TOURS_ENABLED } from '@/tour/index'
export default {
    name: 'EntityEditor',
    props: {
        entity: {
            type: String,
            required: true,
            validator: value => {
                return entities.includes(value)
            },
        },
        id: {
            type: [Number, String],
            required: false,
            validator: value => {
                return !!value
            },
        },
        apiName: {
            type: String,
            required: false,
        },
        copy: {
            type: Boolean,
            required: false,
            default: false,
        },
        newEntity: {
            type: Function,
            required: false,
        },
        readRouteName: {
            type: String,
            required: false,
        },
        eventScopeField: {
            type: String,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            isSaving: false,
            isDirty: false,
            doc: null,
            loadedTeamId: null,
            errorMessage: '',
            requiredRule: v => !!v || 'Required',
            requiredRuleValidZero: v => v === 0 || !!v || 'Required',
        }
    },
    computed: {
        ...mapState(['user']),
        ...mapGetters(['events', 'nextEvent', 'userTeams']),
        toursEnabled() {
            return TOURS_ENABLED
        },
        userPermission() {
            if (this.user && this.user[this.entity]) {
                if (this.isNew) {
                    return this.user[this.entity].owned
                } else if (this.doc && this.doc.teamId) {
                    return this.user.teams.includes(this.doc.teamId)
                        ? this.user[this.entity].owned
                        : this.user[this.entity].shared
                }
                return 0
            } else {
                return 0
            }
        },
        showDocument() {
            return !this.isLoading && this.doc
        },
        apiEntityAddress() {
            return this.apiName || `${this.entity}s`
        },
        disableButtons() {
            return this.isLoading || this.isSaving
        },
        isNew() {
            return !this.id || this.copy
        },
        editState() {
            return this.isNew ? 'Create' : 'Edit'
        },
        teamCount() {
            return this.user && this.user.teams ? this.user.teams.length : 1
        },
        showTeamPicker() {
            return this.teamCount > 1 || this.user[this.entity].all.some(x => x > 1)
        },
        userDefaultTeamId() {
            if (this.user && this.user.defaultTeam) {
                let teamId = parseInt(this.user.defaultTeam)
                if (!isNaN(teamId)) return teamId
            }
            return this.user && this.user.teams ? this.user.teams[0] : 0
        },
        teams() {
            return this.userTeams(this.entity)
        },
        capsEnt() {
            return this.entity[0].toUpperCase() + this.entity.substring(1)
        },
        readRoute() {
            return this.readRouteName ? this.readRouteName : `read${this.capsEnt}`
        },
        isLimitedEdit() {
            if (this.isNew || !this.user || !this.user[this.entity] || !this.doc) return false
            const p = this.user[this.entity]
            const onTeam = !!this.doc.teamId && this.user.teams.includes(this.doc.teamId)
            // Mirror the backend's three-way gate (All / owning-team / shared-team): on-team uses the
            // owned tier, off-team uses the shared tier. Keeping these in sync with CanEdit/CanLimitedEdit
            // is what stops a shared limited editor from being routed to a PUT that the backend will 403.
            const hasFull =
                p.all.includes(permissionsSet.Edit) ||
                (onTeam && p.owned.includes(permissionsSet.Edit)) ||
                (!onTeam && p.shared.includes(permissionsSet.Edit))
            const hasLimited =
                p.all.includes(permissionsSet.EditLimited) ||
                (onTeam && p.owned.includes(permissionsSet.EditLimited)) ||
                (!onTeam && p.shared.includes(permissionsSet.EditLimited))
            return !hasFull && hasLimited
        },
    },
    watch: {
        id(newVal) {
            if (newVal) {
                // Preserve copy semantics on reload: without this.copy the fetch keeps the source
                // keyId (and its real team), which would make the next save POST a keyed model.
                this.getEntity(this.copy)
            } else {
                // Transition INTO the "new" route (e.g. Edit -> New). Vue Router reuses this same
                // component instance across new/edit/copy, so mounted() never re-runs — without an
                // explicit reset the previous encounter's doc leaks into the blank New form.
                this.resetToNew()
            }
        },
        doc: {
            deep: true,
            handler(newVer, oldVer) {
                this.isDirty = newVer && oldVer ? newVer.version === oldVer.version : false
                this.$emit('isDirtyChange', this.isDirty)
            },
        },
        showDocument(val) {
            if (val && !this.isDirty) {
                // startEditorCloseTour returns early if already seen (guarded by localStorage)
                this.$nextTick(() => startEditorCloseTour())
            }
        },
        isDirty(val) {
            if (val) {
                // startEditorDirtyTour returns early if already seen (guarded by localStorage)
                this.$nextTick(() => startEditorDirtyTour())
            }
        },
    },
    mounted() {
        if (this.id) {
            this.getEntity(this.copy)
        } else {
            // Seed teamId with the user's default team before assigning to this.doc, so a new
            // entity opens with the team picker pre-selected. Setting it pre-assignment keeps the
            // doc watcher's first fire a null -> object transition (isDirty stays false); mutating
            // this.doc.teamId afterwards would trip the deep watcher and mark the form dirty.
            this.doc = this.buildNewDoc()
        }
    },
    activated() {
        if (this.isLoading) return
        if (this.id) {
            this.doc = null
            this.getEntity(this.copy)
        } else {
            // Re-entering the New route from keep-alive (e.g. Edit -> Close -> New). activated()
            // fires instead of mounted(), so the cached doc from the previous session must be
            // rebuilt or the "blank" form shows the last entity's data.
            this.resetToNew()
        }
    },
    methods: {
        buildNewDoc() {
            const doc = this.newEntity()
            if (!doc.teamId) {
                doc.teamId = this.userDefaultTeamId
            }
            return doc
        },
        resetToNew() {
            // Edit-only entities never provide a newEntity factory and never reach a New route, but
            // guard anyway so a stray falsy-id transition can't throw.
            if (!this.newEntity) return
            this.doc = this.buildNewDoc()
            this.loadedTeamId = this.doc ? this.doc.teamId : null
            // A freshly reset New form is pristine. The deep doc watcher fires on this object ->
            // object replacement and its version heuristic can misfire (two blank docs both have an
            // undefined version), so force the form clean rather than relying on that comparison.
            this.$nextTick(() => {
                this.isDirty = false
                this.$emit('isDirtyChange', false)
            })
        },
        getEntity(isCopy) {
            //Comparison on String value as that's what is passed from router
            if (this.doc && parseInt(this.doc.keyId) === this.id) return
            this.isLoading = true
            api.get(`/${this.apiEntityAddress}/${this.id}`)
                .then(res => {
                    if (isCopy) {
                        delete res.createdBy
                        delete res.createdDate
                        delete res.keyId
                        delete res.statusId
                        delete res.statusText
                        delete res.teamId
                        delete res.teamName
                        delete res.version
                        // A copy is a fork → a normal team monster; never inherit the Monster Manual flag.
                        delete res.isMonsterManual
                        // A copy is a new entity: seed teamId with the user's default team (set
                        // before assigning to this.doc, same as the newEntity path) so the team
                        // picker opens pre-selected instead of empty.
                        res.teamId = this.userDefaultTeamId
                    }
                    this.doc = res
                    this.loadedTeamId = res ? res.teamId : null
                    this.$emit('entityLoaded', this.doc)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        handleSave(isClose) {
            if (!this.$refs.entityEditForm.validate()) return
            this.isSaving = true
            if (!this.isNew && this.isLimitedEdit) {
                api.patch(`/${this.apiEntityAddress}/${this.id}`, this.doc)
                    .then(res => {
                        this.doc = res
                        this.showSnackbar('success', `${this.entity} submitted for approval`)
                        this.$emit('isDirtyChange', false)
                        if (isClose) this.handleClose()
                    })
                    .catch(res => {
                        if (res.err && res.err.response && res.err.response.status === 409) {
                            this.errorMessage = `${this.entity} NOT SAVED.  It has been updated elsewhere and this is no longer the most recent verions.  Please reload doc.  This will lose your changes`
                        }
                    })
                    .finally(() => {
                        this.isSaving = false
                    })
            } else if (!this.isNew) {
                api.put(`/${this.apiEntityAddress}/${this.id}`, this.doc)
                    .then(res => {
                        const previousTeamId = this.loadedTeamId
                        this.doc = res
                        this.loadedTeamId = res ? res.teamId : null
                        this.showSnackbar('success', `${this.entity} Saved`)
                        this.$emit('saved', { doc: res, previousTeamId })
                        if (isClose) {
                            this.handleClose()
                        }
                    })
                    .catch(res => {
                        if (res.err.response && res.err.response.status === 409) {
                            this.errorMessage = `${this.entity} NOT SAVED.  It has been updated elsewhere and this is no longer the most recent verions.  Please reload doc.  This will lose your changes`
                        }
                    })
                    .finally(() => {
                        this.isSaving = false
                    })
            } else {
                // Only force the default team when the picker was never shown (the user had no way
                // to choose). Gating on teamCount == 1 instead discarded the selection of a
                // single-team user who still sees the picker via cross-team `all` permission
                // (e.g. an admin), overwriting their chosen team with the default on create/copy.
                if (!this.showTeamPicker) {
                    this.doc.teamId = this.userDefaultTeamId
                }
                api.post(`/${this.apiEntityAddress}`, this.doc)
                    .then(res => {
                        this.showSnackbar('success', `${this.entity} Created`)
                        this.$emit('isDirtyChange', false)
                        if (this.eventScopeField) {
                            // Keep the list's focused event in step with where this record landed,
                            // so it is visible on return. Preserve an 'ALL' view (already includes it).
                            const value = this.doc[this.eventScopeField]
                            const current = this.$store.getters.eventSelection(this.entity)
                            if (current !== 'ALL' && current !== value) {
                                this.$store.commit('setEventSelection', { entity: this.entity, eventId: value })
                            }
                        }
                        // Navigate ONCE, and only on success: closing goes back, otherwise open the
                        // created entity's read view. Doing both (push + go(-1)) raced and bounced the
                        // user onto the /copy page, where the id watcher reloaded the source WITH its
                        // keyId — the next save then POSTed a keyed model and the API 500'd.
                        if (isClose) {
                            this.handleClose()
                        } else {
                            this.$router.push({ name: this.readRoute, params: { id: res } })
                        }
                    })
                    .finally(() => {
                        this.isSaving = false
                    })
            }
        },
        handleCancel() {
            if (this.isDirty) {
                this.$confirm({
                    title: 'Discard Changes',
                    body: 'Are you sure you want to discard your changes?',
                    confirmButtonText: 'Discard',
                    cancelButtonText: 'Keep Editing',
                })
                    .then(confirmed => {
                        if (confirmed) {
                            this.isDirty = false
                            this.$emit('isDirtyChange', false)
                            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
                        }
                    })
                    .catch(() => {})
            } else {
                window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
            }
        },
        handleClose() {
            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
        },
        replayEditorTour() {
            resetEditorTours()
            this.$nextTick(() => {
                if (this.isDirty) {
                    startEditorDirtyTour()
                } else {
                    startEditorCloseTour()
                }
            })
        },
    },
}
</script>
